import { LOCKUP_RATIO, MARK } from '@gramas/ui'
import { useEffect, useRef } from 'react'

/** Font size of the word on the splash; the mark's box is derived from it. */
const FONT = 52
const BOX = Math.round(FONT * LOCKUP_RATIO)
const LETTERS = ['r', 'a', 'm', 'a', 's']

export interface BootSplashProps {
  /**
   * `full` is a cold start: the ring arrives as a gauge, becomes the letter,
   * opens the word, and then hands itself to Today's ring. `short` is a resume
   * — the app never went away, so it only gets the lockup and a fade.
   */
  mode: 'full' | 'short'
  /** Whether the session has resolved. The splash never leaves before it has. */
  ready: boolean
  onDone: () => void
}

/**
 * The launch animation.
 *
 * Everything here is a dash, a stroke width or a transform: no layout is read
 * or written mid-sequence except the two measurements the flight needs, so the
 * whole thing runs off the main thread's paint work and stays smooth while the
 * database is opening behind it.
 *
 * Driven imperatively rather than through state because a re-render per beat
 * would fight the transitions it is trying to start.
 */
export function BootSplash({ mode, ready, onDone }: BootSplashProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const shiftRef = useRef<HTMLDivElement>(null)
  const markRef = useRef<SVGSVGElement>(null)
  const turnRef = useRef<SVGGElement>(null)
  const ringRef = useRef<SVGPathElement>(null)
  const barRef = useRef<SVGPathElement>(null)
  const wordRef = useRef<HTMLSpanElement>(null)

  // The sequence is started once and reads these, so that `ready` arriving
  // late — or a tap arriving early — never restarts it.
  const readyRef = useRef(ready)
  readyRef.current = ready
  const doneRef = useRef(onDone)
  doneRef.current = onDone

  useEffect(() => {
    const root = rootRef.current
    const shift = shiftRef.current
    const mark = markRef.current
    const turn = turnRef.current
    const ring = ringRef.current
    const bar = barRef.current
    const word = wordRef.current
    if (!root || !shift || !mark || !turn || !ring || !bar || !word) return

    let cancelled = false
    let skipped = false
    const timers = new Set<ReturnType<typeof setTimeout>>()
    const wakeups = new Set<() => void>()

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        const id = setTimeout(() => {
          timers.delete(id)
          resolve()
        }, ms)
        timers.add(id)
        // A tap cuts every wait short so the sequence unwinds to its end.
        wakeups.add(() => {
          clearTimeout(id)
          timers.delete(id)
          resolve()
        })
      })

    const skip = () => {
      skipped = true
      for (const wake of wakeups) wake()
      wakeups.clear()
    }
    root.addEventListener('pointerdown', skip)

    const ease = 'cubic-bezier(0.22, 0.61, 0.36, 1)'
    const dur = (ms: number) => {
      const t = `${ms}ms ${ease}`
      turn.style.transition = `transform ${t}`
      ring.style.transition = `stroke-dasharray ${t}, stroke-dashoffset ${t}, stroke-width ${t}, opacity 160ms linear`
      bar.style.transition = `stroke-dashoffset ${t} ${Math.round(ms * 0.32)}ms, stroke-width ${t}`
    }

    const gauge = (opacity: number) => {
      ring.style.opacity = String(opacity)
      ring.style.strokeDasharray = `${MARK.gauge.dash} ${MARK.gauge.gap}`
      ring.style.strokeDashoffset = String(MARK.gauge.offset)
      bar.style.strokeDashoffset = String(MARK.barLen)
      turn.style.transform = 'rotate(0deg)'
    }

    const letter = () => {
      ring.style.opacity = '1'
      ring.style.strokeDasharray = `${MARK.letter.dash} ${MARK.letter.gap}`
      ring.style.strokeDashoffset = String(MARK.letter.offset)
      bar.style.strokeDashoffset = '0'
      turn.style.transform = `rotate(${MARK.tilt}deg)`
    }

    /** Slide the row right by half the word, so the mark alone sits centred. */
    const alone = (on: boolean, ms: number) => {
      shift.style.transition = `transform ${ms}ms ${ease}`
      shift.style.transform = on ? `translateX(${word.offsetWidth / 2}px)` : 'translateX(0)'
    }

    // 'hidden' is the state the letters mount in — distinct from 'out', which
    // animates them away and would flash them on screen if used at the start.
    const showWord = (state: 'hidden' | 'in' | 'out') => {
      word.dataset.state = state
    }

    const leave = async () => {
      root.style.opacity = '0'
      await sleep(180)
      if (!cancelled) doneRef.current()
    }

    /** Wait for the session, but never hold a finished animation hostage. */
    const waitReady = async () => {
      while (!readyRef.current && !cancelled && !skipped) await sleep(60)
    }

    /**
     * The session resolving and Today being on screen are not the same instant:
     * React still has to commit the tree. Give it a few frames, then give up —
     * an anonymous launch has no ring to land on and never will.
     */
    const waitForRing = async () => {
      for (let attempt = 0; attempt < 10 && !cancelled; attempt++) {
        const found = document.querySelector('[data-day-ring] svg')
        if (found instanceof SVGSVGElement && found.getBoundingClientRect().width > 0) return found
        await sleep(50)
      }
      return null
    }

    /**
     * Put the mark exactly where Today's ring is — same centre, same radius,
     * same stroke, same arc — and let the real ring take over underneath.
     * Every figure is measured off the live element; none is assumed.
     */
    const flyToRing = (target: SVGSVGElement, ms: number) => {
      const arc = target.querySelector('circle:last-of-type')
      if (!(arc instanceof SVGCircleElement)) return false

      const from = mark.getBoundingClientRect()
      const to = target.getBoundingClientRect()
      if (from.width === 0 || to.width === 0) return false

      const viewBox = target.viewBox.baseVal.width || to.width
      const perUnit = to.width / viewBox
      const dayRadius = arc.r.baseVal.value * perUnit
      const markRadius = (MARK.R / MARK.box) * from.width
      const scale = dayRadius / markRadius
      if (!Number.isFinite(scale) || scale <= 0) return false

      const style = getComputedStyle(arc)
      const dayStroke = Number.parseFloat(style.strokeWidth) * perUnit
      // Same painted thickness on screen once the mark has been scaled up.
      const strokeUnits = (dayStroke * MARK.box) / (from.width * scale)

      const dashes = style.strokeDasharray.split(/[\s,]+/).map(Number.parseFloat)
      const total = (dashes[0] ?? 0) + (dashes[1] ?? 0)
      const progress = total > 0 ? (dashes[0] ?? 0) / total : 0

      dur(ms)
      // dur() holds the crossbar back so it draws after the turn; on the way
      // out there is nothing to wait for, and the delay leaves it hanging in
      // the counter while the rest of the mark is already moving.
      bar.style.transition = `stroke-dashoffset ${Math.round(ms * 0.5)}ms ${ease}, stroke-width ${ms}ms ${ease}`
      mark.style.transition = `transform ${ms}ms ${ease}`
      mark.style.transform = `translate(${to.left + to.width / 2 - (from.left + from.width / 2)}px, ${
        to.top + to.height / 2 - (from.top + from.height / 2)
      }px) scale(${scale})`
      turn.style.transform = 'rotate(0deg)'
      ring.style.strokeWidth = String(strokeUnits)
      ring.style.strokeDasharray = `${progress * MARK.circ} ${(1 - progress) * MARK.circ}`
      ring.style.strokeDashoffset = String(MARK.dayOffset)
      bar.style.strokeWidth = String(strokeUnits)
      bar.style.strokeDashoffset = String(MARK.barLen)
      return true
    }

    const run = async () => {
      // The word's width decides where the mark sits, so the face has to be
      // resolved before anything is placed. Precached, so this is instant —
      // but a slow first install must not stall the launch either.
      await Promise.race([document.fonts.ready, sleep(300)])
      if (cancelled) return

      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
      if (reduced || mode === 'short') {
        dur(0)
        letter()
        alone(false, 0)
        showWord('in')
        if (!reduced) await sleep(420)
        await waitReady()
        return leave()
      }

      dur(0)
      gauge(0)
      ring.style.strokeDasharray = `0 ${MARK.circ}`
      alone(true, 0)
      // Two frames, so the starting state is painted before anything moves.
      await sleep(32)
      if (cancelled) return

      // The ring arrives the way the day's ring does: it sweeps in from twelve.
      dur(460)
      gauge(1)
      await sleep(520)
      if (cancelled) return

      if (!skipped) {
        // The gap slides round and tightens, the whole form leans, and the
        // needle draws inwards until it is the letter's crossbar.
        dur(460)
        letter()
        await sleep(360)
        if (cancelled) return
      } else {
        dur(0)
        letter()
      }

      alone(false, 300)
      showWord('in')
      await sleep(skipped ? 120 : 760)
      if (cancelled) return

      await waitReady()
      if (cancelled) return

      const dayRing = await waitForRing()
      if (cancelled) return
      if (dayRing == null) return leave()

      showWord('out')
      await sleep(200)
      if (cancelled) return

      if (!flyToRing(dayRing, 560)) return leave()
      await sleep(520)
      if (cancelled) return
      return leave()
    }

    void run()

    return () => {
      cancelled = true
      root.removeEventListener('pointerdown', skip)
      for (const id of timers) clearTimeout(id)
    }
  }, [mode])

  return (
    <div
      ref={rootRef}
      // Above every sheet and the tab bar; opaque, so whatever mounts behind it
      // during the sequence is never glimpsed half-built.
      className="fixed inset-0 z-[60] grid place-items-center bg-bg"
      style={{ transition: 'opacity 180ms cubic-bezier(0.22, 0.61, 0.36, 1)' }}
    >
      <div className="flex items-center">
        <div ref={shiftRef}>
          <svg
            ref={markRef}
            viewBox={`0 0 ${MARK.box} ${MARK.box}`}
            width={BOX}
            height={BOX}
            className="block text-accent"
            aria-hidden="true"
          >
            <g ref={turnRef} style={{ transformOrigin: '256px 256px', transformBox: 'view-box' }}>
              <path
                ref={ringRef}
                d={MARK.ring}
                pathLength={MARK.circ}
                fill="none"
                stroke="currentColor"
                strokeWidth={MARK.w}
                strokeLinecap="round"
              />
              <path
                ref={barRef}
                d={MARK.bar}
                fill="none"
                stroke="currentColor"
                strokeWidth={MARK.w}
                strokeLinecap="round"
                strokeDasharray={MARK.barDash}
              />
            </g>
          </svg>
        </div>
        <span
          ref={wordRef}
          data-boot-word
          data-state="hidden"
          aria-hidden="true"
          className="font-display font-semibold text-ink"
          style={{
            fontSize: FONT,
            lineHeight: 1,
            letterSpacing: '-0.03em',
            marginLeft: -FONT * 0.04,
          }}
        >
          {LETTERS.map((glyph, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a fixed literal word
            <span key={i} style={{ '--i': i } as React.CSSProperties}>
              {glyph}
            </span>
          ))}
        </span>
      </div>
      <span className="sr-only">Gramas</span>
    </div>
  )
}
